import { useQuery } from "@tanstack/react-query";
import { ASSISTANT_MODELS, type AssistantModel } from "../lib/models";

/**
 * The model list, fetched live from the assistant worker.
 *
 * The worker calls both providers' /v1/models at request time and filters by rule, so
 * a model released today is selectable today. The bundled list in lib/models.ts is now
 * only a fallback for when that request fails — a dropdown that renders nothing is
 * worse than one that renders a slightly stale list.
 */

export type LiveModel = AssistantModel & { recommended: boolean };

type CataloguePayload = {
  models: { id: string; label: string; provider: "openai" | "anthropic"; recommended: boolean }[];
  default: string;
  errors?: Record<string, string>;
};

/** The bundled list, shaped like the live one, for use when the fetch fails. */
const FALLBACK: LiveModel[] = ASSISTANT_MODELS.map((m) => ({ ...m, recommended: true }));

export function useAvailableModels() {
  const query = useQuery<CataloguePayload>({
    queryKey: ["assistantModels"],
    queryFn: async () => {
      const res = await fetch("/api/assistant-models");
      if (!res.ok) throw new Error(`Model catalogue unavailable (HTTP ${res.status})`);
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const live = query.data?.models;

  return {
    models: live && live.length > 0 ? (live as LiveModel[]) : FALLBACK,
    /** Which model answers when none is chosen, per the worker. */
    defaultModelId: query.data?.default,
    /** True when we are showing the bundled list because the live one did not load. */
    isFallback: !live || live.length === 0,
    /** A provider whose /v1/models call failed — worth surfacing, not worth blocking on. */
    providerErrors: query.data?.errors,
    isLoading: query.isLoading,
  };
}
